import Loader from '@/components/ui/loader';
import { KiaPage_Pagebuilder_Blocks_LaddboxBlock } from '@/graphql/generated/graphql';
import { useForm } from '@tanstack/react-form';
import type { infer as ZodInfer } from 'zod';
import { email, object, string, enum as zenum } from 'zod';

const ownerApprovalOptions = ['Ja', 'Nej', 'Inte relevant'] as const;

const schema = object({
  typeOfLaddbox: string().min(1, 'Välj ett alternativ'),
  firstName: string().min(1, 'Förnamn krävs'),
  lastName: string().min(1, 'Efternamn krävs'),
  street: string().min(1, 'Adress krävs'),
  zipCode: string().regex(/^\d{3}\s?\d{2}$/, 'Ange ett giltigt postnummer'),
  city: string().min(1, 'Stad/ort krävs'),
  email: email('Ange en giltig e-postadress'),
  cemail: email('Ange en giltig e-postadress'),
  phone: string().min(1, 'Telefonnummer krävs'),
  confirmedByBrf: zenum(ownerApprovalOptions, { error: 'Välj ett alternativ' }),
}).refine((data) => data.email === data.cemail, {
  message: 'E-postadresserna matchar inte',
  path: ['cemail'],
});

type FormValues = ZodInfer<typeof schema>;

const inputClass =
  'w-full border border-kia-gray rounded px-4 py-3 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors';
const inputErrorClass =
  'w-full border border-red-400 rounded px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors';
const labelClass = 'block text-sm font-medium mb-1';
const errorClass = 'text-red-500 text-xs mt-1';

export function FieldError({ errors }: { errors: unknown[] }) {
  if (!errors.length) return null;
  const message =
    (errors[0] as { message?: string })?.message ?? String(errors[0]);
  return (
    <p role="alert" aria-live="polite" className={errorClass}>
      {message}
    </p>
  );
}

function LaddboxForm({
  title,
  description,
  laddboxOptions,
}: KiaPage_Pagebuilder_Blocks_LaddboxBlock) {
  const form = useForm({
    defaultValues: {
      typeOfLaddbox: '',
      firstName: '',
      lastName: '',
      street: '',
      zipCode: '',
      city: '',
      email: '',
      cemail: '',
      phone: '',
      confirmedByBrf: undefined as unknown as FormValues['confirmedByBrf'],
    },
    onSubmit: async ({ value }) => {
      const res = await fetch('https://kia-nodemailer.vercel.app/api/booking', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values: value }),
      });

      if (!res.ok) {
        throw new Error(
          'Något gick fel när vi skulle ta emot din beställning, försök igen senare.',
        );
      }
    },
    validators: {
      onSubmit: schema,
    },
  });

  return (
    <section className="wrapper-kia-sm py-12">
      <div className="mb-8">
        {title && <h1 className="font-bold text-4xl mb-1">{title}</h1>}
        {description && <p>{description}</p>}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col gap-6"
      >
        {laddboxOptions && laddboxOptions.length > 0 && (
          <form.Field
            name="typeOfLaddbox"
            children={(field) => (
              <fieldset className="grid gap-2">
                {laddboxOptions.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-start justify-between gap-2 border p-4 rounded-lg transition-colors cursor-pointer has-[:checked]:border-black has-[:checked]:ring-1 has-[:checked]:ring-black has-[:focus-visible]:bg-slate-100 ${field.state.meta.errors.length ? 'border-red-400 bg-red-50/30' : 'border-kia-grey bg-slate-50/50'}`}
                  >
                    <div>
                      <span className="font-semibold text-lg mb-2">
                        {option?.title}
                      </span>
                      <p>{option?.description}</p>
                      <p className="font-bold">{option?.price}</p>
                    </div>
                    <input
                      type="radio"
                      className="ml-auto flex-shrink-0"
                      name={field.name}
                      value={`${option?.title} ${option?.description} ${option?.price}`}
                      checked={
                        field.state.value ===
                        `${option?.title} ${option?.description} ${option?.price}`
                      }
                      onBlur={field.handleBlur}
                      onChange={() =>
                        field.handleChange(
                          `${option?.title} ${option?.description} ${option?.price}`,
                        )
                      }
                    />
                  </label>
                ))}
                <FieldError errors={field.state.meta.errors} />
              </fieldset>
            )}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="firstName"
            children={(field) => (
              <div>
                <label className={labelClass} htmlFor={field.name}>
                  Förnamn
                </label>
                <input
                  className={
                    field.state.meta.errors.length
                      ? inputErrorClass
                      : inputClass
                  }
                  type="text"
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="lastName"
            children={(field) => (
              <div>
                <label className={labelClass} htmlFor={field.name}>
                  Efternamn
                </label>
                <input
                  className={
                    field.state.meta.errors.length
                      ? inputErrorClass
                      : inputClass
                  }
                  type="text"
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          />
        </div>

        <form.Field
          name="street"
          children={(field) => (
            <div>
              <label className={labelClass} htmlFor={field.name}>
                Adress
              </label>
              <input
                className={
                  field.state.meta.errors.length ? inputErrorClass : inputClass
                }
                type="text"
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="zipCode"
            children={(field) => (
              <div>
                <label className={labelClass} htmlFor={field.name}>
                  Postnummer
                </label>
                <input
                  className={
                    field.state.meta.errors.length
                      ? inputErrorClass
                      : inputClass
                  }
                  type="text"
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="city"
            children={(field) => (
              <div>
                <label className={labelClass} htmlFor={field.name}>
                  Stad/ort
                </label>
                <input
                  className={
                    field.state.meta.errors.length
                      ? inputErrorClass
                      : inputClass
                  }
                  type="text"
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          />
        </div>

        <form.Field
          name="email"
          children={(field) => (
            <div>
              <label className={labelClass} htmlFor={field.name}>
                E-post
              </label>
              <input
                className={
                  field.state.meta.errors.length ? inputErrorClass : inputClass
                }
                type="email"
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="cemail"
          children={(field) => (
            <div>
              <label className={labelClass} htmlFor={field.name}>
                Bekräfta e-post
              </label>
              <input
                className={
                  field.state.meta.errors.length ? inputErrorClass : inputClass
                }
                type="email"
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="phone"
          children={(field) => (
            <div>
              <label className={labelClass} htmlFor={field.name}>
                Telefonnr
              </label>
              <input
                className={
                  field.state.meta.errors.length ? inputErrorClass : inputClass
                }
                type="tel"
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="confirmedByBrf"
          children={(field) => (
            <fieldset>
              <legend className={`${labelClass} mb-2`}>
                Har installationen/produkten godkänts av
                fastighetsägaren/bostadsrättsföreningen?
              </legend>
              <div className="flex flex-col sm:flex-row gap-4">
                {(['Ja', 'Nej', 'Inte relevant'] as const).map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <input
                      type="radio"
                      name={field.name}
                      value={option}
                      checked={field.state.value === option}
                      onBlur={field.handleBlur}
                      onChange={() => field.handleChange(option)}
                    />
                    {option}
                  </label>
                ))}
              </div>
              <FieldError errors={field.state.meta.errors} />
            </fieldset>
          )}
        />
        <p className="font-normal text-dark-blue text-xs">
          Jag samtycker till att Kia Sverige AB behandlar mina personuppgifter
          och delar dem med vald leverantör och installationspartner samt att
          installationspartnern kontaktar mig via telefon e-post eller SMS
          angående min beställning.{' '}
          <a
            href="https://www.kia.com/se/integritetspolicy/"
            target="_blank"
            rel="noopener"
          >
            Integritetspolicy
          </a>
        </p>
        <div className="grid gap-2">
          {form.state.isSubmitted && !form.state.errors.length && (
            <p className="text-green-600 text-sm">
              Tack för din beställning, vi hör av oss till dig så snart vi kan!
            </p>
          )}
          {form.state.errors.length > 0 && (
            <p role="alert" aria-live="polite" className={errorClass}>
              {String(form.state.errors[0])}
            </p>
          )}
          <button
            type="submit"
            disabled={form.state.isSubmitting}
            className="bg-black text-white px-8 py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {form.state.isSubmitting ? (
              <Loader className="animate-spin h-4 w-4" />
            ) : (
              'Skicka'
            )}
          </button>
        </div>
      </form>
    </section>
  );
}

export default LaddboxForm;
